import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { CROSS_WORKSPACE_KEYS } from '@/hooks/useSwitchWorkspace'
import { useLeaveWorkspaceScopedRoute } from '@/hooks/useLeaveWorkspaceScopedRoute'

/**
 * Switch the active organisation.
 *
 * This is a TENANT change, and it is deliberately blunter than
 * `useSwitchWorkspace`:
 *
 * - **The workspace selection is cleared.** A workspace belongs to exactly one
 *   org, so carrying one from the org we are leaving would put the two request
 *   headers (`X-Org-Id`, `X-Workspace-Id`) in different tenants. The backend
 *   defends itself — AuthMiddleware moves the workspace into the chosen org —
 *   but the user would see their workspace silently swapped with no
 *   explanation. Clearing it lets AuthProvider re-resolve the new org's
 *   default from `/api/auth/me`.
 * - **Every cached query is dropped, including the account-scoped keys** that
 *   survive a workspace switch (`wallet`, `entitlements`, `userOrgId`, …).
 *   Across an org change those are not account state, they are the *other
 *   tenant's* state — the ones most likely to render someone else's numbers.
 *
 * `removeQueries` rather than `invalidateQueries`: a clean loading state, not
 * a flash of the previous tenant's data.
 */
export function useSwitchOrg() {
  const queryClient = useQueryClient()
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg)
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace)
  const leaveWorkspaceScopedRoute = useLeaveWorkspaceScopedRoute()

  return useCallback(
    (orgId: string | null) => {
      if (useWorkspaceStore.getState().activeOrgId === orgId) return

      setActiveOrg(orgId)
      // setActiveWorkspace(null) also nulls activeWorkspaceName, so no stale
      // label survives into the new org.
      setActiveWorkspace(null)

      queryClient.removeQueries()

      try {
        CROSS_WORKSPACE_KEYS.forEach((k) => localStorage.removeItem(k))
      } catch {
        // Private mode — nothing to clean up.
      }

      // Leave a route whose path names workspace-scoped state. Clearing the
      // caches and localStorage above is not enough on its own: the URL still
      // asserts a project from the org being LEFT, and the build page's restore
      // waterfall treats the URL as its first source, so it was reseeded within
      // the same render. This is the ORG boundary — wider than the workspace
      // one — and it was missed when the navigation was added to
      // useSwitchWorkspace alone.
      leaveWorkspaceScopedRoute()
    },
    [queryClient, setActiveOrg, setActiveWorkspace, leaveWorkspaceScopedRoute],
  )
}
